import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { catchError, finalize, forkJoin, of, Subject, takeUntil } from 'rxjs';

import { IAppointment } from '../../../models/appointment.interface';
import { IClassification } from '../../../models/classification.model';
import { ConditionResponse } from '../../../models/condition.interface';
import { sectionName, SoapNote } from '../../../models/dictation.interface';
import { MedicationRequest } from '../../../models/medical-request.interfcae';
import { AllergyResponse } from '../../../models/allergy.interface';
import { ClassificationService } from '../../../services/classification/classification.service';
import { HistoricalDataService } from '../../../services/historical-data/historical-data.service';

type SummarySourceKind = 'classification' | 'condition';

interface SummarySourceItem {
  kind: SummarySourceKind;
  name: string;
  status: string;
  subText: string;
}

interface SummarySection {
  title: string;
  lines: string[];
}

@Component({
  standalone: false,
  selector: 'cva-consultation-summary-tab',
  templateUrl: './consultation-summary-tab.component.html',
  styleUrl: './consultation-summary-tab.component.scss',
})
export class ConsultationSummaryTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() appointment!: IAppointment;
  @Input() refreshTrigger = 0;

  summaryItems: SummarySourceItem[] = [];
  summarySections: SummarySection[] = [];

  classifications: IClassification[] = [];
  conditions: ConditionResponse[] = [];

  isContextLoading = false;
  isSummaryLoading = false;
  summaryError = '';
  lastGeneratedAt: Date | null = null;

  private readonly destroy$ = new Subject<void>();
  private patientReferenceId = '';

  constructor(
    private readonly classificationService: ClassificationService,
    private readonly historicalDataService: HistoricalDataService,
  ) {}

  ngOnInit(): void {
    this.patientReferenceId = this.resolvePatientReferenceId();

    this.classificationService.classification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.classifications = items || [];
        this.rebuildSummaryItems();
      });

    if (!this.patientReferenceId) {
      this.summaryError = 'Unable to load patient summary because patient context is missing.';
      return;
    }

    this.loadContextAndGenerateSummary();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['refreshTrigger'] &&
      !changes['refreshTrigger'].firstChange &&
      this.patientReferenceId
    ) {
      this.loadContextAndGenerateSummary();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  regenerateSummary(): void {
    if (!this.patientReferenceId || this.isSummaryLoading || this.isContextLoading) {
      return;
    }

    this.generateSummary();
  }

  getItemTooltip(item: SummarySourceItem): string {
    return `${item.name} (${item.status})`;
  }

  trackBySummaryItem(index: number, item: SummarySourceItem): string {
    return `${item.kind}-${item.name}-${item.status}-${index}`;
  }

  trackBySection(index: number, section: SummarySection): string {
    return `${section.title}-${index}`;
  }

  private resolvePatientReferenceId(): string {
    return (
      this.appointment?.patient?.referenceId ||
      this.appointment?.patientId ||
      this.appointment?.patient?.uid ||
      ''
    ).trim();
  }

  private loadContextAndGenerateSummary(): void {
    this.isContextLoading = true;
    this.summaryError = '';

    this.historicalDataService.resetDateRange();
    const { startDate, endDate } = this.historicalDataService.getNextRange();

    forkJoin({
      allergies: this.historicalDataService
        .getAllergiesByPatientId$(this.patientReferenceId)
        .pipe(catchError(() => of([] as AllergyResponse[]))),
      consultations: this.historicalDataService
        .getPreviousConsultationByPatientId$(this.patientReferenceId, startDate, endDate)
        .pipe(catchError(() => of([] as SoapNote[]))),
      medications: this.historicalDataService
        .getMedicationsForPatient$(this.patientReferenceId, startDate, endDate)
        .pipe(catchError(() => of([] as MedicationRequest[]))),
      conditions: this.historicalDataService
        .getConditionsByPatientId$(this.patientReferenceId, startDate, endDate)
        .pipe(catchError(() => of([] as ConditionResponse[]))),
    })
      .pipe(
        finalize(() => {
          this.isContextLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ({ allergies, consultations, medications, conditions }) => {
          this.conditions = conditions
            .filter((condition) => this.isActiveCondition(condition))
            .sort((a, b) =>
              this.getConditionTimestamp(b) - this.getConditionTimestamp(a)
            );
          this.rebuildSummaryItems();
          this.generateSummary(allergies, consultations, medications, this.conditions);
        },
        error: () => {
          this.summaryError = 'Unable to load historical data for patient summary.';
        },
      });
  }

  private isActiveCondition(condition: ConditionResponse): boolean {
    return String(condition.status || '').trim().toLowerCase() === 'active';
  }

  private getConditionTimestamp(condition: ConditionResponse): number {
    const timestamp = new Date(
      condition.recordedDate || condition.onsetDateTime || 0
    ).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private generateSummary(
    allergies?: AllergyResponse[],
    consultations?: SoapNote[],
    medications?: MedicationRequest[],
    conditions?: ConditionResponse[]
  ): void {
    const payload = this.buildSummaryPayload(
      allergies || [],
      consultations || [],
      medications || [],
      conditions || this.conditions
    );

    this.isSummaryLoading = true;
    this.summaryError = '';
    this.summarySections = [];

    this.historicalDataService.generateHistorySummary$(payload)
      .pipe(
        finalize(() => {
          this.isSummaryLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.summarySections = this.mapSummarySections(response);
          this.lastGeneratedAt = new Date();
        },
        error: () => {
          this.summaryError = 'Failed to generate patient summary. Please try again.';
        },
      });
  }

  private buildSummaryPayload(
    allergies: AllergyResponse[],
    consultations: SoapNote[],
    medications: MedicationRequest[],
    conditions: ConditionResponse[]
  ): { history: unknown[] } {
    return {
      history: [
        {
          type: 'allergy history',
          allergies: allergies
            .map((allergy) => {
              const onSetDate = this.toIsoDateOrNull(allergy.onsetDate);
              if (!onSetDate) {
                return null;
              }

              return {
                value: allergy.allergen?.display || '',
                status: allergy.clinicalStatus?.code || '',
                description: allergy.note || '',
                onSetDate,
              };
            })
            .filter((allergy) => !!allergy),
        },
        {
          type: 'historical consultation summary',
          consultations: consultations.map((consultation) => ({
            date: consultation.soapNoteDate || '',
            summary: this.buildConsultationSummary(consultation),
          })),
        },
        {
          type: 'medication history',
          medications: medications.map((medication) => ({
            medicationText: medication.medicationText || '',
            authoredOn: medication.authoredOn || '',
            dosageInstruction: (medication.dosageInstructions || [])
              .map((instruction) => instruction?.timingText || instruction?.routeText || instruction?.method || instruction?.note || '')
              .filter((value) => !!value),
          })),
        },
        {
          type: 'condition history',
          conditions: conditions
            .map((condition) => {
              const conditionOnsetDate = this.toIsoDateOrNull((condition as { onsetDate?: unknown }).onsetDate);
              const onsetDateTime =
                this.toIsoDateOrNull(condition.onsetDateTime) ||
                conditionOnsetDate ||
                this.toIsoDateOrNull(condition.recordedDate);
              const recordedDate =
                this.toIsoDateOrNull(condition.recordedDate) ||
                onsetDateTime;

              if (!onsetDateTime || !recordedDate) {
                return null;
              }

              return {
                condition: condition.condition || '',
                status: condition.status || '',
                onsetDateTime,
                onsetDate: conditionOnsetDate || onsetDateTime,
                recordedDate,
              };
            })
            .filter((condition) => !!condition),
        },
      ]
    };
  }

  private toIsoDateOrNull(value: unknown): string | null {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toISOString();
  }

  private buildConsultationSummary(consultation: SoapNote): string {
    const sections = [
      consultation[sectionName.subjective],
      consultation[sectionName.objective],
      consultation[sectionName.assessment],
      consultation[sectionName.plan],
    ];

    return sections
      .map((section) => this.extractText(section))
      .filter((section) => !!section)
      .join(' | ');
  }

  private extractText(value: unknown): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'object' && 'changingThisBreaksApplicationSecurity' in value) {
      return String((value as { changingThisBreaksApplicationSecurity: unknown }).changingThisBreaksApplicationSecurity || '').trim();
    }

    return '';
  }

  private rebuildSummaryItems(): void {
    const conditionItems: SummarySourceItem[] = this.conditions.map((condition) => ({
      kind: 'condition',
      name: this.normalizeText(condition.condition),
      status: this.normalizeText(condition.status),
      subText: this.formatOnsetDate(condition.onsetDateTime || condition.recordedDate),
    }));

    this.summaryItems = conditionItems;
  }

  private normalizeText(value: unknown): string {
    const normalized = String(value || '').trim();
    return normalized || '—';
  }

  private normalizeDate(value: unknown): string {
    const asText = this.normalizeText(value);
    if (asText === '—') {
      return asText;
    }

    const parsedDate = new Date(asText);
    if (Number.isNaN(parsedDate.getTime())) {
      return asText;
    }

    return parsedDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  private formatOnsetDate(value: unknown): string {
    const formattedDate = this.normalizeDate(value);
    return formattedDate === '—' ? '' : `Onset ${formattedDate}`;
  }

  private mapSummarySections(response: unknown): SummarySection[] {
    const root = response && typeof response === 'object' && 'response' in (response as Record<string, unknown>)
      ? (response as { response: unknown }).response
      : response;

    if (typeof root === 'string') {
      return [{
        title: 'Patient Summary',
        lines: this.toParagraphLines(root),
      }];
    }

    if (Array.isArray(root)) {
      return [{
        title: 'Patient Summary',
        lines: root.map((item) => this.stringifyLine(item)).filter((line) => !!line),
      }];
    }

    if (root && typeof root === 'object') {
      const sections = Object.entries(root as Record<string, unknown>)
        .map(([key, value]) => ({
          title: this.toDisplayTitle(key),
          lines: this.toLines(value),
        }))
        .filter((section) => section.lines.length > 0);

      if (sections.length > 0) {
        return sections;
      }
    }

    return [{
      title: 'Patient Summary',
      lines: ['No summary data available.'],
    }];
  }

  private toParagraphLines(text: string): string[] {
    const lines = text
      .split(/\r?\n+/)
      .map((line) => line.trim())
      .filter((line) => !!line);

    return lines.length > 0 ? lines : ['No summary data available.'];
  }

  private toLines(value: unknown): string[] {
    if (value === null || value === undefined) {
      return [];
    }

    if (typeof value === 'string') {
      return this.toParagraphLines(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((entry) => this.stringifyLine(entry))
        .filter((line) => !!line);
    }

    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, nestedValue]) => `${this.toDisplayTitle(key)}: ${this.stringifyLine(nestedValue)}`)
        .filter((line) => !line.endsWith(': '));
    }

    return [String(value)];
  }

  private stringifyLine(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value).trim();
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.stringifyLine(item)).filter((line) => !!line).join(' | ');
    }

    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, nested]) => `${this.toDisplayTitle(key)}: ${this.stringifyLine(nested)}`)
        .filter((line) => !line.endsWith(': '))
        .join(' | ');
    }

    return '';
  }

  private toDisplayTitle(value: string): string {
    return value
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
  }
}
